import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enUS } from 'date-fns/locale'

import AppRoutes from '@/routes'
import { CommonLoader } from '@/components/common'
import { TENANT_COLORS, STORAGE_KEYS } from '@/constants'
import { ThemeProvider, useThemeController } from '@/styles'
import { forcyteService, localStorageService } from '@/services'
import { DonorProvider, useDonorContext } from '@/context/DonorContext'
import { TenantProvider, useTenantContext } from '@/context/TenantContext'
import {
  ChairpersonProvider,
  useChairpersonContext,
} from '@/context/ChairpersonContext'
import { darkenHexColor, hasTenantTimezone } from '@/utils'

function TenantThemeSync({ onThemeReady }) {
  const { tenant } = useTenantContext()
  const { setPrimaryHex } = useThemeController()

  useEffect(() => {
    if (tenant?.portal_theme) {
      const primaryColor = TENANT_COLORS[tenant.portal_theme]?.primary
      if (primaryColor) {
        setPrimaryHex(primaryColor)
        document.documentElement.style.setProperty(
          '--primary-color',
          primaryColor
        )
        const darkerColor = darkenHexColor(primaryColor, 0.15)
        document.documentElement.style.setProperty(
          '--primary-color-hover',
          darkerColor
        )
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onThemeReady?.(true)
          })
        })
      }
    }
  }, [tenant?.portal_theme, setPrimaryHex, onThemeReady])
  return null
}

function FullPageMessage({ title, body, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        px: 2,
        textAlign: 'center',
      }}
    >
      <ErrorOutlineIcon color='error' sx={{ fontSize: 56 }} />
      <Typography variant='h5' component='h1'>
        {title}
      </Typography>
      <Typography variant='body1'>{body}</Typography>
      {action}
    </Box>
  )
}

function AppContent() {
  const location = useLocation()
  const { getDonor } = useDonorContext()
  const { getChairperson } = useChairpersonContext()
  const { loadingTenant, getTenant, tenant } = useTenantContext()
  const [initializing, setInitializing] = useState(true)
  const [themeReady, setThemeReady] = useState(false)

  useEffect(() => {
    initializeApp()
    checkGuestState()
  }, [])

  useEffect(() => {
    if (tenant && !hasTenantTimezone()) {
      console.error(
        'Donor portal cannot display appointment times: tenant timezone is missing or unrecognized.',
        { tenant_id: tenant.tenant_id, timezone_code: tenant.timezone_code }
      )
    }
  }, [tenant])

  const checkGuestState = () => {
    const isGuest = localStorageService.getItem(STORAGE_KEYS.IS_GUEST)
    if (isGuest) {
      const guestTimestamp = localStorageService.getItem(
        STORAGE_KEYS.IS_GUEST_AT
      )
      if (guestTimestamp) {
        const timestamp = parseInt(guestTimestamp, 10)
        const now = Date.now()
        const twoHoursInMs = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

        // If timestamp is older than 2 hours, clear guest state
        if (now - timestamp > twoHoursInMs) {
          localStorageService.removeItem(STORAGE_KEYS.IS_GUEST)
          localStorageService.removeItem(STORAGE_KEYS.IS_GUEST_AT)
        }
      }
    }
  }

  const initializeApp = async () => {
    setInitializing(true)
    try {
      await getTenant()

      const hasToken = forcyteService.getToken()
      if (!hasToken) return

      // Determine if this is a chairperson or donor based on URL path
      const isChairpersonRoute = location.pathname.startsWith('/chairperson')

      if (isChairpersonRoute) await getChairperson()
      else await getDonor()
    } finally {
      // Always leave the boot gate, otherwise a failed donor/chairperson
      // bootstrap leaves the spinner up and the Retry screen unreachable
      setInitializing(false)
    }
  }

  // Shown both when portal-config fails and when it loads without a usable
  // tenant time zone; the technical reason is logged, the donor gets a retry
  const loadError = (
    <FullPageMessage
      title='Something went wrong'
      body='We couldn’t load the donor portal. Please refresh or try again. If the problem continues, contact support.'
      action={
        <Button variant='contained' onClick={initializeApp}>
          Try again
        </Button>
      }
    />
  )

  if (!initializing && !loadingTenant && !tenant) return loadError

  if (initializing || loadingTenant || !tenant?.portal_theme || !themeReady) {
    return (
      <>
        {tenant?.portal_theme && (
          <TenantThemeSync onThemeReady={setThemeReady} />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '10vh',
          }}
        >
          <CommonLoader color='black' />
        </Box>
      </>
    )
  }

  if (!hasTenantTimezone()) {
    return (
      <main>
        <TenantThemeSync onThemeReady={setThemeReady} />
        {loadError}
      </main>
    )
  }

  return (
    <main>
      <TenantThemeSync onThemeReady={setThemeReady} />
      <AppRoutes />
    </main>
  )
}

function App() {
  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <TenantProvider>
          <DonorProvider>
            <Router>
              <ChairpersonProvider>
                <AppContent />
              </ChairpersonProvider>
            </Router>
          </DonorProvider>
        </TenantProvider>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default App
